import React from 'react';
import {
  Box,
  Chip,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import type {
  MonitoringLocationRegionBreakdown,
  MonitoringLocationSelection,
} from '../../types/monitoring';

type Props = {
  regions: MonitoringLocationRegionBreakdown[];
  selection?: MonitoringLocationSelection | null;
  onRegionSelect?: (region: MonitoringLocationRegionBreakdown) => void;
  onCitySelect?: (region: MonitoringLocationRegionBreakdown, cityName: string) => void;
};

const MonitoringLocationBreakdown: React.FC<Props> = ({
  regions,
  selection = null,
  onRegionSelect,
  onCitySelect,
}) => {
  if (regions.length === 0) {
    return (
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1.5,
          bgcolor: 'background.paper',
          px: 1.5,
          py: 1,
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>
          Aucune localisation sur la carte pour cette période.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1.5,
        bgcolor: 'background.paper',
        overflow: 'hidden',
        height: { xs: 360, lg: 400 },
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          px: 1.5,
          py: 0.75,
          borderBottom: '1px solid',
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 800, fontSize: '0.78rem', display: 'block' }}>
          Répartition géographique
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem', lineHeight: 1.35 }}>
          Cliquez sur une région ou une ville pour zoomer sur la carte.
        </Typography>
      </Box>
      <List
        dense
        disablePadding
        sx={{
          flex: 1,
          overflowY: 'auto',
          py: 0,
          m: 0,
          minHeight: 0,
          '& .MuiListItemButton-root': {
            minHeight: 0,
          },
          '& .MuiListItemText-root': {
            my: 0,
          },
        }}
      >
        {regions.map((region) => {
          const regionSelected = selection?.region === region.region && !selection.city;

          return (
            <Box key={region.region}>
              <ListItemButton
                selected={regionSelected}
                onClick={() => onRegionSelect?.(region)}
                sx={{
                  py: 0.35,
                  px: 1.5,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <ListItemText
                  disableTypography
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.75 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.76rem', lineHeight: 1.3 }}>
                        {region.region}
                      </Typography>
                      <Chip
                        size="small"
                        label={region.count}
                        sx={{
                          height: 18,
                          fontSize: '0.68rem',
                          fontWeight: 700,
                          '& .MuiChip-label': { px: 0.75 },
                        }}
                      />
                    </Box>
                  }
                />
              </ListItemButton>

              {region.cities.map((city) => {
                const citySelected =
                  selection?.region === region.region && selection.city === city.city;

                return (
                  <ListItemButton
                    key={`${region.region}-${city.city}`}
                    selected={citySelected}
                    onClick={() => onCitySelect?.(region, city.city)}
                    sx={{
                      py: 0.25,
                      pl: 3,
                      pr: 1.5,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <ListItemText
                      disableTypography
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.75 }}>
                          <Typography sx={{ fontWeight: 500, fontSize: '0.7rem', lineHeight: 1.3, color: 'text.primary' }}>
                            {city.city}
                          </Typography>
                          <Chip
                            size="small"
                            variant="outlined"
                            label={city.count}
                            sx={{
                              height: 17,
                              fontSize: '0.65rem',
                              fontWeight: 700,
                              '& .MuiChip-label': { px: 0.6 },
                            }}
                          />
                        </Box>
                      }
                    />
                  </ListItemButton>
                );
              })}
            </Box>
          );
        })}
      </List>
    </Box>
  );
};

export default MonitoringLocationBreakdown;
